import { useEffect, useRef } from 'react'
import type { TimelineItem, Track } from '@easyedit/timeline'
import type { MediaAsset } from '../../../lib/api/types'

/** Cada tipo de faixa tem sua cor — reconhecer o tipo de relance é meio caminho da edição. */
const CLIP_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  video: { bg: 'bg-[#1E3A4F]', border: 'border-[#2F5C7A]', text: 'text-[#9FCBE8]' },
  image: { bg: 'bg-[#2A3A52]', border: 'border-[#41567A]', text: 'text-[#A8BEDD]' },
  audio: { bg: 'bg-[#123832]', border: 'border-[#1E5B50]', text: 'text-[#6FD3BE]' },
  text: { bg: 'bg-[#3D2E4A]', border: 'border-[#5D4670]', text: 'text-[#C9A9E0]' },
  caption: { bg: 'bg-[#4A3A18]', border: 'border-[#7A5F24]', text: 'text-[#E8C87D]' },
  shape: { bg: 'bg-[#333B45]', border: 'border-[#4C5866]', text: 'text-[#B3BECB]' },
}

export interface ClipProps {
  item: TimelineItem
  track: Track
  asset?: MediaAsset
  zoom: number
  height: number
  selected: boolean
  onPointerDown: (event: React.PointerEvent, item: TimelineItem, mode: 'move' | 'trim-start' | 'trim-end') => void
}

export function Clip({ item, track, asset, zoom, height, selected, onPointerDown }: ClipProps) {
  const width = Math.max(2, item.duration * zoom)
  const style = CLIP_STYLES[item.kind] ?? CLIP_STYLES.video!
  const fromAI = item.meta?.createdBy === 'AI'

  return (
    <div
      data-item-id={item.id}
      className={`absolute top-0.5 rounded-sm border overflow-hidden group
        ${style.bg} ${selected ? 'border-accent ring-1 ring-accent/60' : style.border}
        ${track.locked ? 'opacity-60' : 'cursor-grab active:cursor-grabbing'}`}
      style={{ left: item.start * zoom, width, height: height - 4 }}
      onPointerDown={(event) => {
        if (track.locked) return
        onPointerDown(event, item, 'move')
      }}
      title={`${item.label ?? item.kind} · ${item.duration.toFixed(2)}s${
        item.meta?.reason ? `\n${String(item.meta.reason)}` : ''
      }`}
    >
      {width > 40 && <ClipBody item={item} asset={asset} zoom={zoom} height={height - 4} />}

      <div className="absolute inset-x-0 top-0 px-1.5 py-0.5 flex items-center gap-1 pointer-events-none">
        <span className={`font-mono text-2xs truncate ${style.text}`}>
          {clipLabel(item, asset)}
        </span>
        {item.speed !== 1 && (
          <span className="font-mono text-2xs text-accent shrink-0">{item.speed}×</span>
        )}
        {/* Um clip criado pela IA fica identificável mesmo depois de aceito. */}
        {fromAI && <span className="text-2xs text-accent shrink-0" title="Criado pela IA">✦</span>}
        {item.groupId && (
          <span className="text-2xs text-ink-300 shrink-0" title="Agrupado — move junto com os irmãos">
            ⛓
          </span>
        )}
        {item.keyframes.length > 0 && (
          <span className="text-2xs text-teal shrink-0" title={`${item.keyframes.length} keyframes`}>◆</span>
        )}
      </div>

      {!track.locked && (
        <>
          <TrimHandle side="start" onPointerDown={(e) => onPointerDown(e, item, 'trim-start')} />
          <TrimHandle side="end" onPointerDown={(e) => onPointerDown(e, item, 'trim-end')} />
        </>
      )}
    </div>
  )
}

function TrimHandle({
  side,
  onPointerDown,
}: {
  side: 'start' | 'end'
  onPointerDown: (event: React.PointerEvent) => void
}) {
  return (
    <div
      className={`absolute top-0 bottom-0 w-2 cursor-ew-resize z-10
        opacity-0 group-hover:opacity-100 transition-opacity
        ${side === 'start' ? 'left-0' : 'right-0'}`}
      onPointerDown={(event) => {
        event.stopPropagation()
        onPointerDown(event)
      }}
    >
      <div className={`absolute top-1 bottom-1 w-1 bg-accent/80 rounded-full ${side === 'start' ? 'left-0.5' : 'right-0.5'}`} />
    </div>
  )
}

function clipLabel(item: TimelineItem, asset?: MediaAsset): string {
  if (item.label) return item.label
  if (item.text) return item.text.content.slice(0, 40)
  if (item.caption) return item.caption.words.map((w) => w.text).join(' ').slice(0, 40) || 'legenda'
  return asset?.filename ?? item.kind
}

/** Miniaturas para vídeo, forma de onda para áudio — o conteúdo do clip precisa ser legível. */
function ClipBody({
  item,
  asset,
  zoom,
  height,
}: {
  item: TimelineItem
  asset?: MediaAsset
  zoom: number
  height: number
}) {
  if (item.kind === 'audio' && asset?.urls?.waveform) {
    return <Waveform url={asset.urls.waveform} item={item} zoom={zoom} height={height} />
  }
  if ((item.kind === 'video' || item.kind === 'image') && asset?.urls?.poster) {
    return (
      <div
        className="absolute inset-0 opacity-30 bg-cover bg-center"
        style={{ backgroundImage: `url(${asset.urls.poster})` }}
      />
    )
  }
  return null
}

interface WaveformData {
  peaks: number[]
  samplesPerPixel: number
  sampleRate: number
  duration: number
}

const waveformCache = new Map<string, Promise<WaveformData>>()

function Waveform({
  url,
  item,
  zoom,
  height,
}: {
  url: string
  item: TimelineItem
  zoom: number
  height: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    let cancelled = false
    // Um mesmo asset aparece em vários clips: baixar os peaks uma vez só é a diferença
    // entre uma timeline fluida e uma rajada de requisições.
    let promise = waveformCache.get(url)
    if (!promise) {
      promise = fetch(url).then((r) => r.json() as Promise<WaveformData>)
      waveformCache.set(url, promise)
    }

    void promise
      .then((data) => {
        if (cancelled) return
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const width = Math.max(2, Math.round(item.duration * zoom))
        canvas.width = width
        canvas.height = height
        ctx.clearRect(0, 0, width, height)
        ctx.fillStyle = 'rgba(111, 211, 190, 0.5)'

        // Os peaks cobrem o asset inteiro; o clip mostra só sua janela de origem.
        const peaksPerSecond = data.sampleRate / data.samplesPerPixel
        const startPeak = Math.floor((item.source?.in ?? 0) * peaksPerSecond)
        const endPeak = Math.floor((item.source?.out ?? data.duration) * peaksPerSecond)
        const span = Math.max(1, endPeak - startPeak)
        const mid = height / 2

        for (let x = 0; x < width; x++) {
          const peakIndex = startPeak + Math.floor((x / width) * span)
          const min = data.peaks[peakIndex * 2] ?? 0
          const max = data.peaks[peakIndex * 2 + 1] ?? 0
          const top = mid - max * mid
          const bottom = mid - min * mid
          ctx.fillRect(x, top, 1, Math.max(1, bottom - top))
        }
      })
      .catch(() => {
        // Sem waveform o clip continua utilizável; só perde o desenho.
      })

    return () => {
      cancelled = true
    }
  }, [url, item.duration, item.source?.in, item.source?.out, zoom, height])

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
}
